/* Hallmark · component: Modal · genre: modern-minimal · theme: maeiz-tokens
 * Sticky header + footer; body scrolls with thin scrollbar; opacity/transform enter-exit
 */
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { clsx } from "clsx";
import { usePresence } from "../../hooks/usePresence";
import { thinScrollbarClass } from "../../styles/overlay";

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    /** Sticky footer — actions stay visible while body scrolls */
    footer?: React.ReactNode;
    size?: "sm" | "md" | "lg" | "xl";
    className?: string;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = "md",
    className,
}) => {
    const { mounted, visible } = usePresence(isOpen, 200);

    useEffect(() => {
        if (!mounted) return;
        document.body.style.overflow = isOpen ? "hidden" : "";
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) onClose();
        };
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", handleEscape);
        };
    }, [mounted, isOpen, onClose]);

    if (!mounted) return null;

    const sizeClasses = {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
    };

    const modalContent = (
        <div
            className={clsx(
                "fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6",
                "bg-black/50 backdrop-blur-[2px] transition-opacity duration-200 ease-out motion-reduce:transition-none",
                visible ? "opacity-100" : "opacity-0",
            )}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={clsx(
                    "flex w-full max-h-[min(90vh,720px)] flex-col overflow-hidden rounded-2xl",
                    "border border-neutral-border bg-white shadow-mz-xl dark:border-white/12 dark:bg-neutral-navy",
                    "transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none motion-reduce:transform-none",
                    visible ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-[0.98] opacity-0",
                    sizeClasses[size],
                    className,
                )}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 z-[1] flex shrink-0 items-start justify-between gap-4 border-b border-neutral-border/70 bg-white px-5 py-4 dark:border-white/10 dark:bg-neutral-navy sm:px-6">
                    {title ? (
                        <h2 className="min-w-0 flex-1 text-lg font-semibold leading-snug text-neutral-dark dark:text-white">
                            {title}
                        </h2>
                    ) : (
                        <span />
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-neutral-secondary hover:bg-neutral-light hover:text-neutral-ink dark:hover:bg-white/10 dark:hover:text-white"
                        aria-label="Close modal"
                    >
                        <X size={18} strokeWidth={2} />
                    </button>
                </div>

                <div className={clsx("min-h-0 flex-1 px-5 py-4 sm:px-6", thinScrollbarClass)}>{children}</div>

                {footer ? (
                    <div className="sticky bottom-0 z-[1] shrink-0 border-t border-neutral-border/70 bg-white px-5 py-4 dark:border-white/10 dark:bg-neutral-navy sm:px-6">
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};
